import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Car, CircleDot, Wrench, Phone, ChevronLeft, ChevronRight, X, Tag, ImagePlus, Trash2, ArrowRight, Star, Heart, Eye, ExternalLink, GripVertical, Sparkles, Shield, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const CATEGORY_META = {
  vehicule: { label: 'Vehicules', icon: Car, color: 'from-blue-500 to-indigo-600', bg: 'bg-blue-50', text: 'text-blue-600' },
  pneu: { label: 'Pneus', icon: CircleDot, color: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', text: 'text-amber-600' },
  materiel: { label: 'Materiel', icon: Wrench, color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', text: 'text-emerald-600' },
};

const CONDITION_LABELS = { neuf: 'Neuf', occasion: 'Occasion', a_reviser: 'A reviser' };
const CONDITION_COLORS = { neuf: 'bg-emerald-100 text-emerald-700', occasion: 'bg-blue-100 text-blue-700', a_reviser: 'bg-orange-100 text-orange-700' };

const MarketplacePage = ({ auth, onLogout }) => {
  const [tab, setTab] = useState('browse');
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [promos, setPromos] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  const loadData = useCallback(async () => {
    try {
      const [listRes, myRes, statsRes, promoRes, favRes] = await Promise.all([
        axios.get(`${API}/marketplace/listings`),
        axios.get(`${API}/marketplace/my-listings`).catch(() => ({ data: [] })),
        axios.get(`${API}/marketplace/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/marketplace/promotions`).catch(() => ({ data: [] })),
        axios.get(`${API}/marketplace/favorites/ids`).catch(() => ({ data: [] })),
      ]);
      setListings(listRes.data);
      setMyListings(myRes.data);
      setStats(statsRes.data);
      setPromos(promoRes.data);
      setFavoriteIds(new Set(favRes.data));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleFavorite = async (e, id) => {
    e.stopPropagation();
    try {
      if (favoriteIds.has(id)) {
        await axios.delete(`${API}/marketplace/favorites/${id}`);
        setFavoriteIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      } else {
        await axios.post(`${API}/marketplace/favorites/${id}`);
        setFavoriteIds(prev => new Set(prev).add(id));
      }
    } catch {}
  };

  const filtered = listings.filter(l => {
    if (catFilter && l.category !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.title || '').toLowerCase().includes(q) || (l.description || '').toLowerCase().includes(q) || (l.seller_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  if (selectedListing) {
    return (
      <AdminLayout auth={auth} onLogout={onLogout}>
        <ListingDetail listing={selectedListing} onBack={() => { setSelectedListing(null); loadData(); }} isFav={favoriteIds.has(selectedListing.id)} onToggleFav={toggleFavorite} />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="marketplace-page">
        {/* Hero Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900 text-white px-6 py-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="marketplace-title">Marketplace Vortekio</h1>
                <p className="text-sm text-slate-300">Achetez, vendez entre professionnels</p>
              </div>
            </div>
            {/* Stats */}
            <div className="flex gap-6 text-sm">
              <div><span className="text-2xl font-bold text-white">{stats.total || 0}</span> <span className="text-slate-400">annonces</span></div>
              <div><span className="text-2xl font-bold text-blue-400">{stats.vehicules || 0}</span> <span className="text-slate-400">vehicules</span></div>
              <div><span className="text-2xl font-bold text-amber-400">{stats.pneus || 0}</span> <span className="text-slate-400">pneus</span></div>
              <div><span className="text-2xl font-bold text-emerald-400">{stats.materiel || 0}</span> <span className="text-slate-400">materiel</span></div>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 py-5 space-y-6">
          {/* Promotional Banners */}
          {promos.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="promo-section">
              {promos.map(p => (
                <div key={p.id}
                  className={`bg-gradient-to-r ${p.gradient} rounded-2xl p-5 text-white relative overflow-hidden cursor-pointer hover:scale-[1.01] transition-transform`}
                  onClick={() => p.cta_url && p.cta_url !== '#' && window.open(p.cta_url, '_blank')}
                  data-testid={`promo-${p.id}`}>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-8 -mb-8" />
                  {p.badge && <Badge className="bg-white/20 text-white text-[10px] mb-2">{p.badge}</Badge>}
                  <h3 className="text-lg font-bold mb-1 relative z-10">{p.title}</h3>
                  <p className="text-sm text-white/80 mb-3 relative z-10">{p.subtitle}</p>
                  {p.cta && (
                    <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border border-white/30 text-xs" data-testid={`promo-cta-${p.id}`}>
                      {p.cta} <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Tabs + Search */}
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <div className="flex gap-2">
              <Button variant={tab === 'browse' ? 'default' : 'outline'} size="sm" onClick={() => setTab('browse')} className="text-xs">Toutes les annonces</Button>
              <Button variant={tab === 'mine' ? 'default' : 'outline'} size="sm" onClick={() => setTab('mine')} className="text-xs">Mes annonces ({myListings.length})</Button>
            </div>
            <div className="flex gap-2 items-center">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-8 h-8 w-52 text-sm" data-testid="marketplace-search" />
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant={catFilter === '' ? 'default' : 'ghost'} onClick={() => setCatFilter('')} className="text-xs h-7">Tout</Button>
                {Object.entries(CATEGORY_META).map(([k, v]) => {
                  const Icon = v.icon;
                  return <Button key={k} size="sm" variant={catFilter === k ? 'default' : 'ghost'} onClick={() => setCatFilter(k)} className="text-xs h-7"><Icon className="w-3 h-3 mr-1" />{v.label}</Button>;
                })}
              </div>
            </div>
          </div>

          {/* Listings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="listings-grid">
            {(tab === 'browse' ? filtered : myListings).map(listing => (
              <ListingCard key={listing.id} listing={listing} onClick={() => setSelectedListing(listing)}
                isFav={favoriteIds.has(listing.id)} onToggleFav={toggleFavorite} />
            ))}
            {(tab === 'browse' ? filtered : myListings).length === 0 && (
              <div className="col-span-full py-12 text-center">
                <Car className="w-12 h-12 mx-auto text-slate-200 mb-3" />
                <p className="text-slate-400">{loading ? 'Chargement...' : 'Aucune annonce trouvee'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// ─── LISTING CARD ───
const ListingCard = ({ listing, onClick, isFav, onToggleFav }) => {
  const cat = CATEGORY_META[listing.category] || CATEGORY_META.materiel;
  const mainPhoto = listing.photos?.[0] || listing.photo_ids?.[0];
  const photoUrl = mainPhoto ? `${API}/marketplace/photos/${typeof mainPhoto === 'string' ? mainPhoto : mainPhoto.file_id}` : null;

  return (
    <Card className="rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all hover:-translate-y-0.5 group" onClick={onClick} data-testid={`listing-${listing.id}`}>
      {/* Photo */}
      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
        {photoUrl ? (
          <img src={photoUrl} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <cat.icon className={`w-12 h-12 ${cat.text} opacity-30`} />
          </div>
        )}
        {/* Overlays */}
        <div className="absolute top-2 left-2 flex gap-1">
          <Badge className={`text-[10px] ${CONDITION_COLORS[listing.condition] || 'bg-slate-100 text-slate-600'}`}>{CONDITION_LABELS[listing.condition] || listing.condition}</Badge>
          {listing.photos?.length > 1 && <Badge className="bg-black/50 text-white text-[10px]"><ImagePlus className="w-2.5 h-2.5 mr-0.5" />{listing.photos.length}</Badge>}
        </div>
        <button className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors" onClick={e => onToggleFav(e, listing.id)}>
          <Heart className={`w-3.5 h-3.5 ${isFav ? 'fill-red-500 text-red-500' : 'text-slate-400'}`} />
        </button>
        {listing.price_public > 0 && (
          <div className="absolute bottom-2 right-2 bg-slate-900/80 text-white px-2.5 py-1 rounded-lg text-sm font-bold backdrop-blur-sm">
            CHF {listing.price_public.toLocaleString('fr-CH')}.-
          </div>
        )}
      </div>
      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-bold text-slate-900 truncate mb-0.5">{listing.title}</h3>
        <p className="text-xs text-slate-500 truncate mb-2">{listing.description?.slice(0, 60)}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-[10px] text-slate-400">
            <Shield className="w-3 h-3" />
            <span className="truncate max-w-[120px]">{listing.seller_name || 'Garage'}</span>
          </div>
          {listing.satisfaction_avg > 0 && (
            <div className="flex items-center gap-0.5">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              <span className="text-xs font-bold text-amber-600">{listing.satisfaction_avg.toFixed(1)}</span>
            </div>
          )}
        </div>
        {listing.garage_notes && (
          <p className="text-[10px] text-indigo-600 mt-1.5 truncate"><Sparkles className="w-2.5 h-2.5 inline mr-0.5" />Note: {listing.garage_notes}</p>
        )}
      </div>
    </Card>
  );
};

// ─── LISTING DETAIL ───
const ListingDetail = ({ listing, onBack, isFav, onToggleFav }) => {
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const photos = listing.photos || [];
  const cat = CATEGORY_META[listing.category] || CATEGORY_META.materiel;

  const prevPhoto = () => setCurrentPhoto(p => (p - 1 + photos.length) % photos.length);
  const nextPhoto = () => setCurrentPhoto(p => (p + 1) % photos.length);

  return (
    <div className="max-w-[1200px] mx-auto p-4 md:p-6" data-testid="listing-detail">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4"><ChevronLeft className="w-4 h-4 mr-1" /> Retour</Button>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Photo Gallery */}
        <div className="space-y-2">
          <div className="aspect-[4/3] bg-slate-100 rounded-xl relative overflow-hidden">
            {photos.length > 0 ? (
              <img src={`${API}/marketplace/photos/${typeof photos[currentPhoto] === 'string' ? photos[currentPhoto] : photos[currentPhoto]?.file_id}`}
                alt={listing.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><cat.icon className={`w-16 h-16 ${cat.text} opacity-20`} /></div>
            )}
            {photos.length > 1 && (
              <>
                <button className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 text-white rounded-full flex items-center justify-center hover:bg-black/60" onClick={prevPhoto}><ChevronLeft className="w-5 h-5" /></button>
                <button className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 text-white rounded-full flex items-center justify-center hover:bg-black/60" onClick={nextPhoto}><ChevronRight className="w-5 h-5" /></button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">{currentPhoto + 1}/{photos.length}</div>
              </>
            )}
          </div>
          {/* Thumbnails */}
          {photos.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {photos.map((p, i) => (
                <img key={i} src={`${API}/marketplace/photos/${typeof p === 'string' ? p : p?.file_id}`}
                  alt={`Photo ${i + 1}`}
                  className={`w-14 h-14 rounded-lg object-cover cursor-pointer border-2 shrink-0 ${i === currentPhoto ? 'border-blue-500' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  onClick={() => setCurrentPhoto(i)} />
              ))}
            </div>
          )}
        </div>

        {/* Info Panel */}
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`bg-gradient-to-r ${cat.color} text-white text-xs`}>{cat.label}</Badge>
              <Badge className={`text-xs ${CONDITION_COLORS[listing.condition]}`}>{CONDITION_LABELS[listing.condition]}</Badge>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{listing.title}</h1>
            <div className="text-3xl font-black text-slate-900">CHF {(listing.price_public || 0).toLocaleString('fr-CH')}.-</div>
            {listing.price_pro > 0 && listing.price_pro !== listing.price_public && (
              <p className="text-sm text-slate-500">Prix pro: CHF {listing.price_pro.toLocaleString('fr-CH')}.-</p>
            )}
          </div>

          {listing.description && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-1">Description</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{listing.description}</p>
            </div>
          )}

          {listing.garage_notes && (
            <Card className="p-3 bg-indigo-50 rounded-lg border-indigo-200">
              <h3 className="text-xs font-bold text-indigo-700 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Note du garage</h3>
              <p className="text-sm text-indigo-600">{listing.garage_notes}</p>
            </Card>
          )}

          {listing.satisfaction_avg > 0 && (
            <Card className="p-3 bg-amber-50 rounded-lg border-amber-200">
              <div className="flex items-center gap-2">
                <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`w-4 h-4 ${i <= Math.round(listing.satisfaction_avg) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />)}</div>
                <span className="text-sm font-bold text-amber-700">{listing.satisfaction_avg.toFixed(1)}/5</span>
                <span className="text-xs text-slate-500">satisfaction client</span>
              </div>
            </Card>
          )}

          <Card className="p-3 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-700">{listing.seller_name}</span>
            </div>
            {listing.seller_phone && (
              <a href={`tel:${listing.seller_phone}`} className="flex items-center gap-2 text-sm text-blue-600 mt-1 hover:underline">
                <Phone className="w-3.5 h-3.5" /> {listing.seller_phone}
              </a>
            )}
          </Card>

          <div className="flex gap-2">
            <Button className="flex-1 bg-slate-900" onClick={() => listing.seller_phone && window.open(`tel:${listing.seller_phone}`)}>
              <Phone className="w-4 h-4 mr-1" /> Contacter
            </Button>
            <Button variant="outline" onClick={e => onToggleFav(e, listing.id)}>
              <Heart className={`w-4 h-4 ${isFav ? 'fill-red-500 text-red-500' : ''}`} />
            </Button>
          </div>

          <div className="flex items-center gap-3 text-[10px] text-slate-400">
            <span><Eye className="w-3 h-3 inline mr-0.5" />{listing.views || 0} vues</span>
            <span><Clock className="w-3 h-3 inline mr-0.5" />{listing.created_at?.slice(0, 10)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplacePage;
